<template>
  <div class="max-w-5xl mx-auto">
    <Head title="Detalle de Inventario" />
    
    <!-- Header & Actions -->
    <div class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <div>
        <h1 class="text-3xl font-bold text-gray-800 flex items-center gap-2">
          <Link href="/inventories" class="text-gray-400 hover:text-orange-500 transition-colors font-bold">
            Inventario
          </Link>
          <span class="text-gray-300 font-light">/</span>
          <span>{{ getFormattedDate(inventory?.created_at) }}</span>
        </h1>
        <p class="text-sm text-gray-500 mt-0.5">
          Registrado por <span class="font-medium text-gray-700">{{ inventory?.user ? (inventory.user.first_name + ' ' + inventory.user.last_name) : (user?.first_name + ' ' + user?.last_name) }}</span>
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <div
          v-if="inventory?.status" :class="{
            'px-3 py-1.5 rounded-lg text-sm font-bold border flex items-center gap-2': true,
            'bg-yellow-50 text-yellow-700 border-yellow-200': inventory.status === 'request',
            'bg-green-50 text-green-700 border-green-200': inventory.status === 'process',
            'bg-red-50 text-red-700 border-red-200': inventory.status === 'canceled'
          }"
        >
          <i
            class="fa" :class="{
              'fa-clock': inventory.status === 'request',
              'fa-check-circle': inventory.status === 'process',
              'fa-ban': inventory.status === 'canceled'
            }"
          />
          {{ inventory.status === 'request' ? 'Solicitud Pendiente' : (inventory.status === 'canceled' ? 'Inventario Anulado' : 'Procesado') }}
        </div>
      </div>
    </div>

    <!-- Tabla de productos -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
      <div class="p-6 border-b border-gray-100 flex justify-between items-center">
        <h3 class="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <i class="fa fa-boxes text-orange-500" />
          Detalle de Productos
        </h3>
        <span class="text-sm text-gray-500 bg-gray-50 px-3 py-1 rounded-lg">
          Total items: <span class="font-bold text-gray-700">{{ products?.total || 0 }}</span>
        </span>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap text-sm text-left">
          <thead>
            <tr class="bg-gray-50/50 text-gray-500 border-b border-gray-100">
              <th class="py-3 px-6 font-medium uppercase tracking-wider text-xs">Código</th>
              <th class="py-3 px-6 font-medium uppercase tracking-wider text-xs">Producto</th>
              <th class="py-3 px-6 font-medium uppercase tracking-wider text-xs">Depósito</th>
              <th class="py-3 px-6 font-medium uppercase tracking-wider text-xs text-right">Cantidad</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr 
              v-for="product in products?.data || []" 
              :key="product.id" 
              class="hover:bg-gray-50 transition-colors"
            >
              <td class="py-3 px-6 font-mono text-gray-600">{{ product?.product?.code || 'N/A' }}</td>
              <td class="py-3 px-6 font-medium text-gray-900">{{ product?.product?.name || 'Producto' }}</td>
              <td class="py-3 px-6 text-gray-600">
                <span class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                  <i class="fa fa-warehouse text-gray-400 text-[10px]" />
                  {{ product?.deposit?.name || 'Sin depósito' }}
                </span>
              </td>
              <td class="py-3 px-6 text-right font-medium text-gray-900 text-base">
                {{ formatQuantity(product?.quantity || 0) }}
              </td>
            </tr>
            <tr v-if="!products?.data?.length">
              <td colspan="4" class="py-12 px-6 text-center text-gray-400">
                <div class="flex flex-col items-center">
                  <div class="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                    <i class="fa fa-box-open text-3xl opacity-50" />
                  </div>
                  <span class="font-medium">No hay productos registrados en este inventario</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Pagination inside the card footer -->
      <div v-if="products?.links && products.links.length > 3" class="px-6 py-4 border-t border-gray-100 bg-gray-50">
        <pagination :links="products.links" />
      </div>
    </div>

    <!-- Actions -->
    <div class="flex justify-end items-center gap-4 mb-8">
      <Link 
        href="/inventories"
        class="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm cursor-pointer"
      >
        <i class="fa fa-arrow-left mr-1.5 text-xs text-gray-400" /> Volver al listado
      </Link>

      <button 
        v-if="canAnnul"
        type="button" 
        class="px-5 py-2.5 bg-white border border-red-300 text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors shadow-sm flex items-center gap-2"
        @click="confirmAnnul"
      >
        <i class="fa fa-ban" />
        Anular Inventario
      </button>

      <button 
        v-if="canApprove"
        class="px-6 py-2.5 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 transition-colors shadow-sm flex items-center gap-2" 
        @click="confirmApproval"
      >
        <i class="fa fa-check-circle" />
        Aprobar Inventario
      </button>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput.vue'
import Pagination from '@/Shared/Pagination.vue'

export default {
  components: {
    Head,
    Icon,
    Link,
    TextInput,
    Pagination,
  },

  layout: Layout,

  props: {
    inventory: {
      type: Object,
      required: true,
    },
    products: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
  },

  computed: {
    canApprove() {
      return this.inventory?.status === 'request' && 
             (!this.user?.role || ['storer', 'admin', 'superadmin', 'owner'].includes(this.user?.role?.toLowerCase?.()) || this.user?.owner)
    },
    canAnnul() {
      return this.inventory?.status === 'request' && 
             (!this.user?.role || ['storer', 'admin', 'superadmin', 'owner'].includes(this.user?.role?.toLowerCase?.()) || this.user?.owner)
    },
  },

  methods: {
    getFormattedDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleString('es-VE', { 
        dateStyle: 'long',
        timeStyle: 'short',
        hour12: false, 
      })
    },

    formatQuantity(qty) {
      return new Intl.NumberFormat('es-VE').format(qty || 0)
    },

    confirmApproval() {
      if (confirm('¿Está seguro que desea aprobar este inventario?')) {
        this.$inertia.get(`/inventories/approve/${this.inventory?.id}`)
      }
    },

    confirmAnnul() {
      if (confirm('¿Está seguro de que desea anular esta solicitud de inventario?')) {
        this.$inertia.post(`/inventories/${this.inventory.id}/annul`)
      }
    },
  },
}
</script>
